import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button } from '../common';
import { APIResponse } from '@memriti/shared';
import { PracticeSession, practiceService, SessionStatistics } from '../../api/services/practice';

interface PracticeSummaryProps {
  session: APIResponse<PracticeSession>;
}

export const PracticeSummary: React.FC<PracticeSummaryProps> = ({ session }) => {
  const navigate = useNavigate();
  const { data } = session;
  const [statistics, setStatistics] = useState<SessionStatistics | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    const loadStatistics = async () => {
      try {
        const statsResponse = await practiceService.getSessionStatistics(data.id);
        setStatistics(statsResponse.data);
      } catch (error) {
        console.error('Failed to load session statistics:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    loadStatistics();
  }, [data.id]);

  // Handle both session_items and practice_session_items field names
  const sessionItems = data.session_items || data.practice_session_items || [];
  const totalItems = sessionItems.length || 0;
  const score = totalItems > 0 ? (data.correct_items / totalItems) * 100 : 0;
  const timePerQuestion = totalItems > 0 ? data.total_time / totalItems : 0;

  return (
    <Card className="p-6 max-w-2xl mx-auto dark:bg-gray-800">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-2 dark:text-white">Practice Session Complete!</h2>
        <p className="text-gray-600 dark:text-gray-400">Great job completing your practice session</p>
      </div>

      <div className="grid grid-cols-2 gap-6 mb-8">
        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
          <h3 className="text-lg font-semibold mb-2 dark:text-white">Score</h3>
          <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{score.toFixed(1)}%</p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
          <h3 className="text-lg font-semibold mb-2 dark:text-white">Correct Answers</h3>
          <p className="text-3xl font-bold text-green-600 dark:text-green-400">
            {data.correct_items}/{totalItems}
          </p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
          <h3 className="text-lg font-semibold mb-2 dark:text-white">Session Duration</h3>
          <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
            {data.duration_minutes}m
          </p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
          <h3 className="text-lg font-semibold mb-2 dark:text-white">Avg. Time per Question</h3>
          <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">
            {Math.floor(timePerQuestion)}s
          </p>
        </div>
      </div>

      {/* Session Statistics */}
      {!loadingStats && statistics && (
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4 dark:text-white">Session Statistics</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">Unique Questions</h4>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {statistics.unique_questions}
              </p>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-orange-800 dark:text-orange-200 mb-1">Repeated Questions</h4>
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {statistics.repeated_questions}
              </p>
            </div>
          </div>

          {statistics.repeated_questions > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg mb-4">
              <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">Question Repetition</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-yellow-700 dark:text-yellow-300">Asked once:</span>
                  <span className="font-medium">{statistics.question_breakdown.asked_once}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-yellow-700 dark:text-yellow-300">Asked twice:</span>
                  <span className="font-medium">{statistics.question_breakdown.asked_twice}</span>
                </div>
                {statistics.question_breakdown.asked_thrice_plus > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-yellow-700 dark:text-yellow-300">Asked 3+ times:</span>
                    <span className="font-medium">{statistics.question_breakdown.asked_thrice_plus}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">By Content Type</h4>
              <div className="space-y-1">
                {Object.entries(statistics.by_content_type).map(([type, count]) => (
                  <div key={type} className="flex justify-between text-sm">
                    <span className="capitalize">{type.replace('_', ' ')}:</span>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">By Reason</h4>
              <div className="space-y-1">
                {Object.entries(statistics.by_reason).map(([reason, count]) => (
                  <div key={reason} className="flex justify-between text-sm">
                    <span className="capitalize">{reason.replace('_', ' ')}:</span>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-4">
        <Button
          onClick={() => navigate('/dashboard')}
          variant="secondary"
          className="flex-1"
        >
          Back to Dashboard
        </Button>
        <Button
          onClick={() => navigate('/practice')}
          variant="primary"
          className="flex-1"
        >
          Practice Again
        </Button>
      </div>
    </Card>
  );
};